<template>
  <div class="pallet-list">
    <div class="header-buttons" v-if="showHeaderOps">
      <el-button type="primary" plain size="mini" @click="refreshPage">
        Refresh
      </el-button>
    </div>
    <basic-table
      v-loading="loadingData"
      :table-data="stockPage"
      :table-headers="tableHeaders"
      :has-actions="showActions"
      :sortChange="sortChange"
      :paginationChange="paginationChange"
      :actionWidth="actionWidth"
      :showPagination="showPagination"
      :hasRowExpand="true"
      :summary="summary"
      rowKey="id"
    >
      <template v-slot:row-actions="props">
        <div class="actions f-r">
          <slot
            name="actions"
            v-bind:row="props.row"
            v-bind:index="props.index"
          />
        </div>
      </template>
      <template v-slot:footer-ops>
        <el-button type="primary" plain size="mini" @click="refreshPage">
          Refresh
        </el-button>
      </template>
      <template v-slot:totalPalletCount="props">
        <span class="in-cart-count bold-oswald">
          {{ props.row.palletIds.length }}
        </span>
      </template>
      <template v-slot:row-expand="props">
        <div class="group-expands">
          <div class="tables f-c">
            <pallet-list
              :palletQuery="{
                ids: props.row.palletIds
              }"
              :showActions="false"
              :showGroupName="false"
              :showHeaderOps="false"
            />
            <packing-list
              :queryParams="
                props.row.goodsIds.length > 0
                  ? { ids: props.row.goodsIds }
                  : null
              "
              :showActions="false"
              :showHeaderOps="false"
            />
          </div>
        </div>
      </template>
    </basic-table>
  </div>
</template>
<script>
import debounce from 'lodash/debounce'
import isEmpty from 'lodash/isEmpty'
// mixin
import { pageJumps } from '@/core/mixin/pages'
// models
import {
  NotIncludeDeleted,
  StockShippedTableHeaders
} from '@/core/models/pallet'
// components
import BasicTable from '@/components/common/BasicTable.vue'
import PalletList from '@/components/pallet/PalletList.vue'
import PackingList from '@/components/goods/PackingList.vue'
import { searchStock } from '@/api/pallet'
import {
  Ascending,
  DefaultPageSize,
  Descending,
  PageCurrentKey,
  PageSizeKey
} from '@/core/configs/types'
import { queryAddress } from '@/api/address'
import { LocalOrder } from '@/core/models/order'
export default {
  name: 'StockList',
  mixins: [pageJumps],
  props: {
    stockQuery: {
      type: Object,
      required: true
    },
    refresh: {
      type: Boolean,
      default: false
    },
    showActions: {
      type: Boolean,
      default: true
    },
    showPagination: {
      type: Boolean,
      default: true
    },
    showHeaderOps: {
      type: Boolean,
      default: true
    },
    actionWidth: {
      type: Number,
      default: 80
    },
    excludeCols: {
      type: Array,
      default: () => []
    },
    summary: {
      type: Boolean,
      default: true
    }
  },
  components: { BasicTable, PalletList, PackingList },
  data() {
    return {
      stockList: null,
      pagination: {
        [PageSizeKey]: this.showPagination ? DefaultPageSize : 10,
        [PageCurrentKey]: 1,
        total: 0,
        asc: '',
        desc: ''
      },
      loadingData: true,
      debouncedQuery: debounce(this.fetchStock, 300),
      prevQuery: null
    }
  },
  computed: {
    stockPage() {
      return {
        records: this.stockList,
        ...this.pagination
      }
    },
    tableHeaders() {
      return StockShippedTableHeaders.filter(
        one => !this.excludeCols.includes(one.key)
      )
    },
    fetchQuery() {
      return {
        ...this.stockQuery,
        ...this.pagination,
        statusList: NotIncludeDeleted
      }
    }
  },
  methods: {
    sortChange(sort) {
      if (sort.order === Ascending) {
        this.pagination.asc = sort.col
        this.pagination.desc = ''
      } else if (sort.order === Descending) {
        this.pagination.desc = sort.col
        this.pagination.asc = ''
      }
      this.debouncedQuery()
    },
    paginationChange(key, path, value) {
      this.pagination[key] = value
      this.debouncedQuery()
    },
    refreshPage() {
      this.debouncedQuery()
    },
    async fetchStock() {
      this.loadingData = true
      let page
      if (!isEmpty(this.stockQuery)) {
        page = await searchStock(this.fetchQuery)
        const addressIds = page.records
          .filter(one => !!one.addressId)
          .map(one => one.addressId)
        let addressMap = {}
        if (addressIds.length > 0) {
          const addressPage = await queryAddress({ addressIds, size: addressIds.length })
          addressMap = Object.fromEntries(
            addressPage.records.map(one => [one.id, one])
          )
        }
        page.records.forEach(one => {
          if (one.type === LocalOrder) one.groupName = ''
          one.id = one.orderId + one.addressId + one.groupName
          one.goodsIds = one.goodsIds?.split(',') ?? []
          one.palletIds = one.palletIds.split(',')
          one.address = addressMap[one.addressId]
        })
      }
      const records = page?.records ?? []
      // search address
      this.$set(this, 'stockList', records)
      this.pagination.total = page?.total ?? 0
      this.loadingData = false
    }
  },
  watch: {
    refresh: {
      handler() {
        this.debouncedQuery()
      },
      immediate: true
    },
    stockQuery: {
      handler(one, two) {
        if (JSON.stringify(one) === JSON.stringify(two)) return
        this.debouncedQuery()
      }
    }
  }
}
</script>
<style lang="scss" scoped>
@import '@/assets/styles/common.scss';

.header-buttons {
  margin-bottom: 10px;
}

.group-expands {
  .actions {
    margin-bottom: 10px;

    .el-button {
      font-size: 14px;
    }
  }

  .tables {
    gap: 20px;

    .pallet-list {
      min-height: 0;
    }

    @extend .inner-table-override;
  }

  @extend .global-margin;
}
</style>
